<template>
    <TransitionRoot
        as="template"
        :show="state.open"
    >
        <Dialog
            as="div"
            class="relative z-6000"
            :open="state.open === true"
            @close="dialogClose(state.overlayPreventClose)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                        enter-to="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                    >
                        <DialogPanel
                            :class="[
                                state.maxWidthMax === true > 0
                                    ? `sm:w-full sm:max-w-5xl`
                                    : 'sm:w-full sm:max-w-xl',
                                'relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:p-6',
                            ]"
                        >
                            <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                <button
                                    type="button"
                                    class="cursor-pointer rounded-md bg-white text-gray-400 hover:text-gray-500"
                                    @click="dialogClose()"
                                >
                                    <span class="sr-only">Close</span>
                                    <MdiClose
                                        class="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div class="select-none sm:flex sm:items-start">
                                <div
                                    class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-200 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <MdiApplicationBrackets
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <DialogTitle
                                        as="h3"
                                        class="text-base leading-6 font-semibold text-gray-900"
                                    >
                                        {{ state.title }}
                                    </DialogTitle>
                                    <div class="mt-2">
                                        <p class="text-sm text-gray-500">
                                            {{ state.text }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div
                                class="mt-5 select-none sm:ml-14 sm:flex sm:justify-start sm:gap-6"
                            >
                                <SwitchGroup
                                    as="div"
                                    class="flex items-center gap-2"
                                >
                                    <Switch
                                        v-model="schemaConfig.exportDuid"
                                        :class="[
                                            schemaConfig.exportDuid ? 'bg-cyan-400' : 'bg-gray-200',
                                            'focus:ring-none relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden',
                                        ]"
                                    >
                                        <span
                                            aria-hidden="true"
                                            :class="[
                                                schemaConfig.exportDuid
                                                    ? 'translate-x-5'
                                                    : 'translate-x-0',
                                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out',
                                            ]"
                                        />
                                    </Switch>
                                    <SwitchLabel
                                        as="span"
                                        class="text-sm"
                                    >
                                        <span
                                            v-show="schemaConfig.exportDuid === false"
                                            class="font-medium text-gray-900"
                                        >
                                            {{ t('buttonExportDuid') }}
                                        </span>
                                        <span
                                            v-show="schemaConfig.exportDuid === true"
                                            class="font-medium text-gray-900"
                                        >
                                            {{ t('buttonExportDuid') }}
                                        </span>
                                    </SwitchLabel>
                                </SwitchGroup>

                                <SwitchGroup
                                    as="div"
                                    class="flex items-center gap-2"
                                >
                                    <Switch
                                        v-model="schemaConfig.exportRecipient"
                                        :class="[
                                            schemaConfig.exportRecipient
                                                ? 'bg-cyan-400'
                                                : 'bg-gray-200',
                                            'focus:ring-none relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden',
                                        ]"
                                    >
                                        <span
                                            aria-hidden="true"
                                            :class="[
                                                schemaConfig.exportRecipient
                                                    ? 'translate-x-5'
                                                    : 'translate-x-0',
                                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out',
                                            ]"
                                        />
                                    </Switch>
                                    <SwitchLabel
                                        as="span"
                                        class="text-sm"
                                    >
                                        <span
                                            v-show="schemaConfig.exportRecipient === false"
                                            class="font-medium text-gray-900"
                                        >
                                            {{ t('buttonExportRecipient') }}
                                        </span>
                                        <span
                                            v-show="schemaConfig.exportRecipient === true"
                                            class="font-medium text-gray-900"
                                        >
                                            {{ t('buttonExportRecipient') }}
                                        </span>
                                    </SwitchLabel>
                                </SwitchGroup>
                            </div>

                            <div class="mt-5 select-none sm:flex sm:justify-end sm:gap-2">
                                <ActionButton
                                    :value="t('buttonClose')"
                                    state="neutral"
                                    :icon="MdiChevronLeft"
                                    @click="dialogClose()"
                                />
                                <ActionButton
                                    :value="t('buttonDownloadCSVFile')"
                                    state="info"
                                    :icon="MdiDownload"
                                    :is-disabled="saving.inter === false"
                                    @click="downloadCSVSchemaFile()"
                                />
                                <ActionButton
                                    :value="t('buttonDownloadJSONFile')"
                                    state="info"
                                    :icon="MdiDownload"
                                    :is-disabled="saving.inter === false"
                                    @click="downloadJSONSchemaFile()"
                                />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { reactive, watch } from 'vue';
import ActionButton from '../ui/ActionButton.vue';
import { t } from '../composables/i18n';
import { uuidCreate, sortObjectByKey } from '../composables/utils';
import slugify from 'slugify';
import {
    getTemplatePlaceholderFromRef,
    mapUsedPlaceholders,
    dumpCSV,
} from '../composables/useTemplateSchema';
import {
    Switch,
    SwitchGroup,
    SwitchLabel,
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';
import {
    MdiApplicationBrackets,
    MdiClose,
    MdiDownload,
    MdiChevronLeft,
} from 'materialdesignicons-vue3/icons/';

defineExpose({
    name: 'TemplateSchemaModal',
});

const props = defineProps({
    templateVersionModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
    templateModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
    usedPlaceholders: {
        type: Object,
        default: () => {
            return {};
        },
    },
    state: {
        type: Object,
        default: () => {
            return {
                open: false,
                title: '',
                text: '',
                maxWidthMax: false,
                data: {},
                overlayPreventClose: false,
            };
        },
    },
});

const state = props.state;
const templateVersionModel = props.templateVersionModel;
const templateModel = props.templateModel;
const usedPlaceholders = props.usedPlaceholders;
const previewSampleData = reactive({
    templateCode: templateModel.code,
    langCode: templateVersionModel.langCode,
    version: templateVersionModel.version,
    duid: uuidCreate(),
    //performAction: ['PREVIEW'],
    valueCollection: {},
    recipient: {
        email: '',
    },
});

const CSV = {
    fileName: `TEMPLATING-EXPORT-SCHEMA-${slugify(previewSampleData.templateCode)}.csv`,
    content: 'data;error',
};

const saving = reactive({
    active: false,
    inter: false,
    text: `${t('loading')}`,
});
const allPlaceholderGroups = reactive({
    data: {},
});

const schemaConfig = reactive({
    exportDuid: false,
    exportRecipient: false,
});

const downloadJSONSchemaFile = () => {
    const JSONData = [sortObjectByKey(previewSampleData)];
    const blob = new Blob([JSON.stringify(JSONData, null, 4)], {
        type: 'application/json',
    });
    const linkSource = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = linkSource;
    downloadLink.download = `TEMPLATING-EXPORT-SCHEMA-${slugify(
        previewSampleData.templateCode,
    )}.json`;
    downloadLink.click();
};

const downloadCSVSchemaFile = () => {
    const blob = new Blob([CSV.content], { type: 'text/csv' });
    const linkSource = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');

    downloadLink.href = linkSource;
    downloadLink.download = CSV.fileName;
    downloadLink.click();
};

watch(schemaConfig, async () => {
    await syncData(false);
});

const syncData = async (reloadPlaceholderGroups = false) => {
    if (reloadPlaceholderGroups === true || Object.keys(allPlaceholderGroups.data).length === 0) {
        allPlaceholderGroups.data = await getTemplatePlaceholderFromRef(
            templateVersionModel.templateVersionId,
        );
    }
    previewSampleData.valueCollection = mapUsedPlaceholders(
        usedPlaceholders,
        allPlaceholderGroups.data,
    );
    if (schemaConfig.exportDuid === true) {
        previewSampleData.duid = uuidCreate();
    } else if (Object.keys(previewSampleData).includes('duid')) {
        delete previewSampleData.duid;
    }
    if (schemaConfig.exportRecipient === true) {
        if (!Object.keys(previewSampleData).includes('recipient')) {
            previewSampleData.recipient = {
                email: '',
            };
        }
    } else if (Object.keys(previewSampleData).includes('recipient')) {
        delete previewSampleData.recipient;
    }
    CSV.content = dumpCSV(sortObjectByKey(previewSampleData));
};

watch(state, async (state) => {
    if (state.open === true) {
        saving.active = true;
        await syncData(true);
        saving.active = false;
        saving.inter = true;
    } else {
        saving.inter = false;
    }
});

const dialogClose = (preventClose = false) => {
    if (preventClose === false) {
        state.open = false;
    }
};
</script>
